import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import {
  Input,
  Modal,
  ModalCloseButton,
  Select,
  ToggleInput,
} from 'components/Shared';
import * as Tabs from 'components/Shared/Tabs';
import { MODAL_DISMISS_ACTION } from 'constants/Common';
import type { CreatePhysicalDocksBulkPayload } from 'contracts/PhysicalDocks';
import { useValidation } from 'hooks';
import React, {
  forwardRef,
  ForwardRefExoticComponent,
  RefAttributes,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from 'store';
import {
  CreatePhysicalDockActions as CreateActions,
  CreatePhysicalDocksBulkActions as CreateBulkActions,
  FetchPhysicalDockInventoryActions as InventoryActions,
} from 'store/ducks/physicalDocks';
import { ListWarehouseBuildingsActions } from 'store/ducks/warehouseBuildings';
import {
  CreatePhysicalDocksBulkValidator,
  CreatePhysicalDockValidator,
  hasPhysicalDockRangeConflict,
  MAX_PHYSICAL_DOCK_NUMBER,
  physicalDockRangeConflictMessage,
} from 'validators/PhysicalDocks';
import { groupBuildingsByArea } from '../helpers';
import * as S from './styles';

export interface Ref {
  openModal: () => void;
}

interface Props {
  warehouseId: string | number;
  onCreate?: () => void;
}

interface IPhysicalDockCreationModal extends ForwardRefExoticComponent<
  Props & RefAttributes<Ref>
> {}

type AttemptKind = 'individual' | 'bulk';

interface ActiveAttempt {
  id: number;
  kind: AttemptKind;
}

interface BulkFormData {
  buildingId: string | number;
  baseName: string;
  startNumber: string | number;
  endNumber: string | number;
  blocked?: string | boolean;
}

const BULK_INITIAL_DATA: Omit<BulkFormData, 'buildingId'> = {
  baseName: 'Doca',
  startNumber: 1,
  endNumber: 1,
  blocked: false,
};

const PhysicalDockCreationModal: IPhysicalDockCreationModal = forwardRef<
  Ref,
  Props
>(({ warehouseId, onCreate }, ref) => {
  const dispatch: AppDispatch = useDispatch();
  const individualFormRef = useRef<FormHandles>(null);
  const bulkFormRef = useRef<FormHandles>(null);
  const generationRef = useRef(0);
  const inventoryGenerationRef = useRef(0);
  const activeAttemptRef = useRef<ActiveAttempt | null>(null);
  const isOpenRef = useRef(false);
  const { handleFormErrors, handleApiErrors } = useValidation();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [bulkBaseName, setBulkBaseName] = useState('Doca');
  const [bulkBuildingId, setBulkBuildingId] = useState<number | null>(null);
  const [bulkStartNumber, setBulkStartNumber] = useState(1);
  const [bulkEndNumber, setBulkEndNumber] = useState(1);

  const { loading: creatingDock, validationErrors } = useSelector(
    (state: RootState) => state.createPhysicalDock
  );
  const {
    loading: creatingDocksBulk,
    errorMessage: bulkErrorMessage,
    validationErrors: bulkValidationErrors,
  } = useSelector((state: RootState) => state.createPhysicalDocksBulk);
  const bulkBackendErrorMessage =
    bulkValidationErrors.find(({ message }) => Boolean(message))?.message ||
    bulkErrorMessage;
  const { data: inventory, loading: loadingInventory } = useSelector(
    (state: RootState) => state.fetchPhysicalDockInventory
  );
  const { data: buildings, loading: loadingBuildings } = useSelector(
    (state: RootState) => state.listWarehouseBuildings
  );
  const buildingOptions = useMemo(
    () => groupBuildingsByArea(buildings),
    [buildings]
  );
  const selectedBuildingName = useMemo(
    () =>
      buildings.find(({ id }) => id === bulkBuildingId)?.name ??
      String(bulkBuildingId ?? ''),
    [buildings, bulkBuildingId]
  );
  const rangeConflictMessage = physicalDockRangeConflictMessage(
    selectedBuildingName
  );
  const selectedRanges = useMemo(
    () =>
      inventory.find((item) => item.buildingId === bulkBuildingId)?.ranges ||
      [],
    [bulkBuildingId, inventory]
  );
  const formattedRanges = useMemo(
    () =>
      selectedRanges
        .map(({ start, end }) =>
          start === end ? `${start}` : `${start}–${end}`
        )
        .join(', '),
    [selectedRanges]
  );
  const bulkCount = bulkEndNumber - bulkStartNumber + 1;
  const hasValidBulkRange =
    Number.isSafeInteger(bulkStartNumber) &&
    Number.isSafeInteger(bulkEndNumber) &&
    bulkStartNumber > 0 &&
    bulkEndNumber >= bulkStartNumber &&
    bulkStartNumber <= MAX_PHYSICAL_DOCK_NUMBER &&
    bulkEndNumber <= MAX_PHYSICAL_DOCK_NUMBER &&
    bulkCount <= 500;
  const hasBulkConflict = useMemo(
    () =>
      hasValidBulkRange &&
      hasPhysicalDockRangeConflict(
        selectedRanges,
        bulkStartNumber,
        bulkEndNumber
      ),
    [bulkEndNumber, bulkStartNumber, hasValidBulkRange, selectedRanges]
  );
  const bulkSubmitDisabled =
    creatingDocksBulk ||
    loadingBuildings ||
    loadingInventory ||
    !bulkBuildingId ||
    !bulkBaseName.trim() ||
    !hasValidBulkRange ||
    hasBulkConflict;
  const isSubmitting = creatingDock || creatingDocksBulk;

  const resetBulkState = useCallback(() => {
    setBulkBaseName('Doca');
    setBulkBuildingId(null);
    setBulkStartNumber(1);
    setBulkEndNumber(1);
  }, []);

  const invalidateAttempts = useCallback(() => {
    generationRef.current += 1;
    inventoryGenerationRef.current += 1;
    activeAttemptRef.current = null;
  }, []);

  const isCurrentInventoryGeneration = useCallback(
    (generation: number) =>
      isOpenRef.current && inventoryGenerationRef.current === generation,
    []
  );

  const isCurrentAttempt = useCallback(
    (attempt: ActiveAttempt) =>
      isOpenRef.current &&
      activeAttemptRef.current?.id === attempt.id &&
      activeAttemptRef.current.kind === attempt.kind,
    []
  );

  const onModalClose = useCallback(() => {
    isOpenRef.current = false;
    invalidateAttempts();
    setIsOpen(false);
    setActiveTab(0);
    resetBulkState();
    individualFormRef.current?.reset();
    individualFormRef.current?.setErrors({});
    bulkFormRef.current?.setData(BULK_INITIAL_DATA);
    bulkFormRef.current?.setErrors({});
    dispatch(CreateActions.reset());
    dispatch(CreateBulkActions.reset());
    dispatch(InventoryActions.reset());
    dispatch(ListWarehouseBuildingsActions.reset());
  }, [dispatch, invalidateAttempts, resetBulkState]);

  const onSuccess = useCallback(() => {
    onModalClose();
    onCreate?.();
  }, [onCreate, onModalClose]);

  const beginAttempt = useCallback(
    (kind: AttemptKind): ActiveAttempt | null => {
      if (activeAttemptRef.current !== null || !isOpenRef.current) return null;

      const attempt = { id: generationRef.current + 1, kind };
      generationRef.current = attempt.id;
      activeAttemptRef.current = attempt;
      return attempt;
    },
    []
  );

  const onIndividualSubmit = useCallback(
    async (data: any): Promise<void> => {
      if (creatingDock) return;
      const attempt = beginAttempt('individual');
      if (!attempt) return;

      try {
        individualFormRef.current?.setErrors({});
        Object.assign(data, { warehouseId });

        const validData =
          await new CreatePhysicalDockValidator().schema.validate(data, {
            abortEarly: false,
          });
        if (!isCurrentAttempt(attempt)) return;

        dispatch(
          CreateActions.request(
            validData,
            () => {
              if (!isCurrentAttempt(attempt)) return false;
              onSuccess();
              return true;
            },
            () => {
              if (!isCurrentAttempt(attempt)) return false;
              activeAttemptRef.current = null;
              return true;
            }
          )
        );
      } catch (error) {
        if (!isCurrentAttempt(attempt)) return;
        activeAttemptRef.current = null;
        handleFormErrors(error, individualFormRef);
      }
    },
    [
      beginAttempt,
      creatingDock,
      dispatch,
      handleFormErrors,
      isCurrentAttempt,
      onSuccess,
      warehouseId,
    ]
  );

  const onBulkSubmit = useCallback(
    async (data: BulkFormData): Promise<void> => {
      if (bulkSubmitDisabled) return;
      const attempt = beginAttempt('bulk');
      if (!attempt) return;

      try {
        bulkFormRef.current?.setErrors({});
        const payload: CreatePhysicalDocksBulkPayload = {
          warehouseId: Number(warehouseId),
          buildingId: Number(data.buildingId),
          baseName: data.baseName.trim(),
          startNumber: Number(data.startNumber),
          endNumber: Number(data.endNumber),
          blocked: data.blocked === '1' || data.blocked === true,
        };
        const validData =
          await new CreatePhysicalDocksBulkValidator().schema.validate(
            payload,
            {
              abortEarly: false,
            }
          );
        if (!isCurrentAttempt(attempt)) return;

        if (
          hasPhysicalDockRangeConflict(
            selectedRanges,
            validData.startNumber,
            validData.endNumber
          )
        ) {
          activeAttemptRef.current = null;
          return;
        }

        dispatch(
          CreateBulkActions.request(
            validData,
            () => {
              if (!isCurrentAttempt(attempt)) return false;
              onSuccess();
              return true;
            },
            () => {
              if (!isCurrentAttempt(attempt)) return false;
              activeAttemptRef.current = null;
              return true;
            }
          )
        );
      } catch (error) {
        if (!isCurrentAttempt(attempt)) return;
        activeAttemptRef.current = null;
        handleFormErrors(error, bulkFormRef);
      }
    },
    [
      beginAttempt,
      bulkSubmitDisabled,
      dispatch,
      handleFormErrors,
      isCurrentAttempt,
      onSuccess,
      selectedRanges,
      warehouseId,
    ]
  );

  useEffect(() => {
    handleApiErrors(validationErrors, individualFormRef);
  }, [handleApiErrors, validationErrors]);

  useEffect(() => {
    handleApiErrors(bulkValidationErrors, bulkFormRef);
  }, [activeTab, bulkValidationErrors, handleApiErrors]);

  useEffect(() => {
    if (isOpen) {
      const inventoryGeneration = inventoryGenerationRef.current;
      const isCurrentInventoryRequest = () =>
        isCurrentInventoryGeneration(inventoryGeneration);

      dispatch(ListWarehouseBuildingsActions.reset());
      dispatch(ListWarehouseBuildingsActions.request(warehouseId));
      dispatch(
        InventoryActions.request(
          Number(warehouseId),
          isCurrentInventoryRequest,
          isCurrentInventoryRequest
        )
      );
    }
  }, [dispatch, isCurrentInventoryGeneration, isOpen, warehouseId]);

  useEffect(
    () => () => {
      isOpenRef.current = false;
      invalidateAttempts();
      dispatch(CreateActions.reset());
      dispatch(CreateBulkActions.reset());
      dispatch(InventoryActions.reset());
      dispatch(ListWarehouseBuildingsActions.reset());
    },
    [dispatch, invalidateAttempts]
  );

  const onModalOpen = useCallback(() => {
    invalidateAttempts();
    isOpenRef.current = true;
    setActiveTab(0);
    resetBulkState();
    setIsOpen(true);
  }, [invalidateAttempts, resetBulkState]);

  const onTabChange = useCallback(
    (index: number) => {
      if (isSubmitting || activeAttemptRef.current !== null) return;
      if (index === 1) resetBulkState();
      setActiveTab(index);
    },
    [isSubmitting, resetBulkState]
  );

  useImperativeHandle(ref, () => ({ openModal: onModalOpen }), [onModalOpen]);

  return (
    <Modal isOpen={isOpen} onClickOutside={onModalClose}>
      <S.ModalContent style={{ maxWidth: '840px' }}>
        <S.ModalHeader>
          <h4>Nova Doca Operacional</h4>
          <ModalCloseButton onClick={onModalClose} />
        </S.ModalHeader>
        <S.ModalBody>
          <S.TabsContainer $disabled={isSubmitting} disabled={isSubmitting}>
            <Tabs.Controller activeTab={activeTab} onTabChange={onTabChange}>
              <Tabs.ModalTab title="Cadastro individual" />
              <Tabs.ModalTab title="Cadastro em lote" />
            </Tabs.Controller>
          </S.TabsContainer>

          {activeTab === 0 ? (
            <Form ref={individualFormRef} onSubmit={onIndividualSubmit}>
              <S.FormRow>
                <Input name="name" label="Nome" />
              </S.FormRow>
              <S.FormRow>
                <Input name="number" type="number" label="Número" />
                <Select
                  name="buildingId"
                  label="Prédio"
                  options={buildingOptions}
                  isLoading={loadingBuildings}
                  isClearable
                  menuPortalTarget={document.body}
                />
              </S.FormRow>
              <S.FormRow>
                <ToggleInput name="blockedAt" label="Bloqueada" />
              </S.FormRow>
              <S.FormActions>
                <S.Button type="button" mood="light" onClick={onModalClose}>
                  {MODAL_DISMISS_ACTION}
                </S.Button>
                <S.Button
                  type="submit"
                  aria-label="Salvar"
                  disabled={creatingDock}
                >
                  {creatingDock ? <S.ActivityIndicator /> : 'Salvar'}
                </S.Button>
              </S.FormActions>
            </Form>
          ) : (
            <Form
              ref={bulkFormRef}
              initialData={BULK_INITIAL_DATA}
              onSubmit={onBulkSubmit}
            >
              <S.FormRow>
                <Select
                  name="buildingId"
                  label="Prédio"
                  options={buildingOptions}
                  isLoading={loadingBuildings}
                  isClearable
                  menuPortalTarget={document.body}
                  onChange={(option) =>
                    setBulkBuildingId(option ? Number(option.value) : null)
                  }
                />
                <Input
                  name="baseName"
                  label="Nome base das docas"
                  onChange={(event) => setBulkBaseName(event.target.value)}
                />
              </S.FormRow>
              <S.FormRow>
                <Input
                  name="startNumber"
                  type="number"
                  min={1}
                  max={MAX_PHYSICAL_DOCK_NUMBER}
                  label="Número inicial"
                  onChange={(event) =>
                    setBulkStartNumber(Number(event.target.value))
                  }
                />
                <Input
                  name="endNumber"
                  type="number"
                  min={1}
                  max={MAX_PHYSICAL_DOCK_NUMBER}
                  label="Número final"
                  onChange={(event) =>
                    setBulkEndNumber(Number(event.target.value))
                  }
                />
              </S.FormRow>
              <S.FormRow>
                <ToggleInput
                  name="blocked"
                  label="Criar todas as docas como bloqueadas"
                />
              </S.FormRow>

              {formattedRanges && (
                <S.InventoryText>
                  Já cadastradas neste prédio: {formattedRanges}
                </S.InventoryText>
              )}
              {hasValidBulkRange && (
                <S.BatchPreview>
                  <strong>{bulkCount} docas serão criadas</strong>
                  <span>
                    {bulkBaseName.trim()} {bulkStartNumber} até{' '}
                    {bulkBaseName.trim()} {bulkEndNumber}
                  </span>
                </S.BatchPreview>
              )}
              {(hasBulkConflict || bulkBackendErrorMessage) && (
                <S.ConflictAlert
                  role="alert"
                  aria-label={
                    hasBulkConflict
                      ? rangeConflictMessage
                      : bulkBackendErrorMessage || undefined
                  }
                >
                  {hasBulkConflict
                    ? rangeConflictMessage
                    : bulkBackendErrorMessage}
                </S.ConflictAlert>
              )}

              <S.FormActions>
                <S.Button type="button" mood="light" onClick={onModalClose}>
                  {MODAL_DISMISS_ACTION}
                </S.Button>
                <S.Button
                  type="submit"
                  aria-label="Criar docas"
                  disabled={bulkSubmitDisabled}
                >
                  {creatingDocksBulk ? (
                    <span role="status" aria-label="Criando docas">
                      <S.ActivityIndicator aria-hidden="true" />
                    </span>
                  ) : (
                    'Criar docas'
                  )}
                </S.Button>
              </S.FormActions>
            </Form>
          )}
        </S.ModalBody>
      </S.ModalContent>
    </Modal>
  );
});

export default PhysicalDockCreationModal;
